import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FiSun, FiUsers } from 'react-icons/fi';
import { BsPeople } from 'react-icons/bs';
import { LuSprout, LuBaby, LuHousePlug, LuMusic2 } from 'react-icons/lu';
import { FaArrowRight } from 'react-icons/fa';
import Header from '../Common/Header';
import Footer from '../Common/Footer';
import HeroCarousel from '../Common/HeroCarousel';
import { getAllSermons } from '../../Redux/slices/sermonSlice';
import { getAllEvents } from '../../Redux/slices/eventSlice';
import bibleImage from '../../Assets/Bible.png';
import readingImage from '../../Assets/Reading.png';
import worship2Image from '../../Assets/worship 2.png';
import worshipImage from '../../Assets/worship.png';
import waterImage from '../../Assets/Water.png';
import event1Image from '../../Assets/event 1.png';
import event2Image from '../../Assets/Event 2.png';
import event3Image from '../../Assets/Event 3.png';
import handsImage from '../../Assets/Hands.png';

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sermons, loading: sermonsLoading } = useSelector((state) => state.sermons);
  const { events, loading: eventsLoading } = useSelector((state) => state.events);

  useEffect(() => {
    // Fetch latest 3 sermons
    dispatch(getAllSermons({ page: 1, limit: 3 }));
    // Fetch upcoming 3 events
    dispatch(getAllEvents({ upcoming: true, limit: 3 }));
  }, [dispatch]);

  // Get fallback images for sermons
  const sermonFallbackImages = [bibleImage, worship2Image, worshipImage];
  
  // Get fallback images for events
  const eventFallbackImages = [event1Image, event2Image, event3Image];

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get latest 3 sermons or use empty array
  const latestSermons = sermons.slice(0, 3);
  
  // Get upcoming 3 events or use empty array
  const upcomingEvents = events.slice(0, 3);
  return (
    <>
      <Header />
      <div className="min-h-screen">
      {/* Hero Section with Carousel */}
      <HeroCarousel 
        title={<>Welcome to<br />House of Praise</>}
        subtitle="Experience the power of God through healing and prophetic ministry. Join us in worship, fellowship, and witness miraculous transformations in a spirit-filled community."
        showButton={true}
        buttonText="Get in Touch with US"
        buttonAction={() => navigate('/contact')}
      />

      {/* Worship & Healing Section */}
      <div className="bg-gray-100 py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center font-serif mb-4">
            Join Us for Worship & Healing
          </h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12 font-sf-pro">
            Experience the power of healing and prophetic ministry in our services.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Sunday Morning Service */}
            <div className="bg-white rounded-lg shadow-md p-20 flex flex-col items-center text-center border-2 border-transparent hover:border-orange-500 transition-colors duration-300">
              <div className="bg-orange-100 p-4 rounded-full mb-4">
                <FiSun className="text-orange-500 text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-2 font-sf-pro">Sunday Morning Worship</h3>
              <p className="text-orange-500 font-medium mb-4 font-sf-pro">8:00 AM</p>
              <p className="text-gray-600 font-sf-pro">
                Traditional and contemporary worship services with inspiring messages and uplifting songs.
              </p>
            </div>

            {/* Wednesday Evening Service */}
            <div className="bg-white rounded-lg shadow-md p-20 flex flex-col items-center text-center border-2 border-transparent hover:border-purple-500 transition-colors duration-300">
              <div className="bg-purple-100 p-4 rounded-full mb-4">
                <BsPeople className="text-purple-500 text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-2 font-sf-pro">Wednesday Evening</h3>
              <p className="text-purple-500 font-medium mb-4 font-sf-pro">5:00 PM</p>
              <p className="text-gray-600 font-sf-pro">
                Hour of celebration for spiritual growth and community connection.
              </p>
            </div>

            {/* Small Groups */}
            <div className="bg-white rounded-lg shadow-md p-20 flex flex-col items-center text-center border-2 border-transparent hover:border-blue-500 transition-colors duration-300">
              <div className="bg-blue-100 p-4 rounded-full mb-4">
                <FiUsers className="text-blue-500 text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-2 font-sf-pro">Small Groups</h3>
              <p className="text-blue-500 font-medium mb-4 font-sf-pro">Various Times</p>
              <p className="text-gray-600 font-sf-pro">
                Connect with others in intimate settings for fellowship, study, and mutual support.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* About Our Ministry Section */}
      <div className="relative bg-cover bg-center py-20"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${waterImage})` 
        }}>
        <div className="container mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-center">
          <div className="w-full md:w-1/2 mb-10 md:mb-0 pr-0 md:pr-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-sf-pro">About Our Ministry</h2>
            <p className="text-gray-300 mb-4 font-sf-pro">
              House of Praise Healing and Prophetic Ministries is dedicated to bringing spiritual and physical healing through the power of faith. We believe in the gifts of prophecy and healing as vital expressions of God's love.
            </p>
            <p className="text-gray-300 mb-6 font-sf-pro">
              Our mission is to create an atmosphere where people can encounter God's healing presence, receive prophetic direction, and grow in their spiritual journey. We embrace both the compassionate and revelatory aspects of ministry.
            </p>
            <button 
              onClick={() => navigate('/ministries')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition duration-300 font-sf-pro"
            >
              Learn more about our Ministry
            </button>
          </div>
          <div className="w-full md:w-1/2">
            <img src={readingImage} alt="People in worship" className="rounded-lg shadow-xl w-full" />
          </div>
        </div>
      </div>

      {/* Sermons Section */}
      <div className="bg-gray-100 py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-2 font-sf-pro">Sermons</h2>
          <p className="text-gray-600 font-sf-pro">Listen to our recent messages.</p>
        </div>
        
        {sermonsLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading sermons...</p>
          </div>
        ) : latestSermons.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {latestSermons.map((sermon, index) => (
              <div key={sermon._id} className="bg-white rounded-lg shadow-md overflow-hidden h-full">
                <img 
                  src={sermon.thumbnailUrl || sermonFallbackImages[index % 3]} 
                  alt={sermon.title} 
                  className="w-full h-64 object-cover" 
                />
                <div className="p-8">
                  <h3 className="text-xl font-bold mb-2 font-sf-pro line-clamp-2">{sermon.title}</h3>
                  <p className="text-gray-500 mb-2 font-sf-pro">{formatDate(sermon.date)}</p>
                  <p className="text-gray-600 mb-4 font-sf-pro text-sm">By {sermon.preacher}</p>
                  <Link 
                    to={`/sermon/${sermon._id}`} 
                    className="text-blue-600 hover:text-blue-800 font-medium font-sf-pro"
                  >
                    Listen Now
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">No sermons available yet. Check back soon!</p>
          </div>
        )}
      </div>

      {/* Upcoming Events Section */}
      <div className="relative bg-cover bg-center py-20"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${waterImage})` 
        }}>
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 font-sf-pro">Upcoming Events</h2>
            <p className="text-gray-300 font-sf-pro">Don't miss these special opportunities to connect and grow.</p>
          </div>
          
          {eventsLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              <p className="mt-4 text-white">Loading events...</p>
            </div>
          ) : upcomingEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {upcomingEvents.map((event, index) => (
                <div key={event._id} className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm rounded-lg overflow-hidden border border-gray-600">
                  <img 
                    src={event.imageUrl || eventFallbackImages[index % 3]} 
                    alt={event.title} 
                    className="w-full h-48 object-cover" 
                  />
                  <div className="p-6">
                    <p className="text-blue-400 mb-2 font-sf-pro">{formatDate(event.startDate)}</p>
                    <h3 className="text-xl font-bold text-white mb-2 font-sf-pro line-clamp-2">{event.title}</h3>
                    <p className="text-gray-300 mb-4 font-sf-pro line-clamp-2">
                      {event.description || 'Join us for this special event.'}
                    </p>
                    <Link 
                      to="/events" 
                      className="text-blue-400 hover:text-blue-300 font-medium font-sf-pro"
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-white">No upcoming events at the moment. Check back soon!</p>
            </div>
          )}
        </div>
      </div>

      {/* Our Ministries Section */}
      <div className="bg-gray-100 py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-2 font-sf-pro">Our Ministries</h2>
            <p className="text-gray-600 font-sf-pro">Find your place to serve and grow</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Life-Stage Ministries */}
            <div className="text-center bg-white rounded-2xl p-8 transition-all duration-300 hover:bg-purple-600 hover:text-white group cursor-pointer">
              <div className="bg-purple-200 group-hover:bg-white rounded-full p-6 w-32 h-32 mx-auto mb-6 flex items-center justify-center transition-all duration-300">
                <LuBaby className="text-purple-600 text-4xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 font-sf-pro">Life-Stage Ministries</h3>
              <p className="text-gray-600 font-sf-pro group-hover:text-white">
                Ministries designed to meet you where you are in life, providing age-appropriate discipleship and community connection.
              </p>
            </div>

            {/* Outreach & Service */}
            <div className="text-center bg-white rounded-2xl p-8 transition-all duration-300 hover:bg-teal-700 hover:text-white group cursor-pointer">
              <div className="bg-teal-200 group-hover:bg-white rounded-full p-6 w-32 h-32 mx-auto mb-6 flex items-center justify-center transition-all duration-300">
                <LuHousePlug className="text-teal-700 text-4xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 font-sf-pro">Outreach & Service</h3>
              <p className="text-gray-600 font-sf-pro group-hover:text-white">
                Taking God's love beyond our walls to serve our community and share the Gospel with those in need.
              </p>
            </div>

            {/* Worship & Creative Arts */}
            <div className="text-center bg-white rounded-2xl p-8 transition-all duration-300 hover:bg-blue-600 hover:text-white group cursor-pointer">
              <div className="bg-blue-200 group-hover:bg-white rounded-full p-6 w-32 h-32 mx-auto mb-6 flex items-center justify-center transition-all duration-300">
                <LuMusic2 className="text-blue-600 text-4xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 font-sf-pro">Worship & Creative Arts</h3>
              <p className="text-gray-600 font-sf-pro group-hover:text-white">
                Using our creative gifts to lead others into the presence of God through music, media, and artistic expression.
              </p>
            </div>

            {/* Support & Growth */}
            <div className="text-center bg-white rounded-2xl p-8 transition-all duration-300 hover:bg-orange-500 hover:text-white group cursor-pointer">
              <div className="bg-orange-200 group-hover:bg-white rounded-full p-6 w-32 h-32 mx-auto mb-6 flex items-center justify-center transition-all duration-300">
                <LuSprout className="text-orange-500 text-4xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 font-sf-pro">Support & Growth</h3>
              <p className="text-gray-600 font-sf-pro group-hover:text-white">
                Ministries focused on personal development, spiritual growth, and providing support during life's challenges and transitions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Ready to Take Your Next Step Section */}
      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="h-96 md:h-auto">
            <img src={handsImage} alt="Hands joined together" className="w-full h-full object-cover" />
          </div>
          <div className="bg-gray-900 text-white p-12 flex items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-sf-pro">Ready to Take Your Next Step?</h2>
              <p className="text-gray-300 mb-8 font-sf-pro">
                We'd love to hear from you. Reach out with any question, prayer requests, or to schedule a healing session.
              </p>
              <Link 
                to="/contact"
                className="border border-white text-white hover:bg-white hover:text-gray-900 font-medium py-3 px-8 rounded-md transition duration-300 font-sf-pro inline-flex items-center gap-2 group"
              >
                Contact us
                <FaArrowRight className="opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-200" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
      <Footer />
    </>
  );
};

export default Home;